import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Toggle from '../ui/Toggle';
import { LabelWithHelp } from '../ui/FieldHelp';
import { COURSE_SETTINGS_FIELD_HELP as HELP } from './courseSettingsFieldHelp';
import { Btn, Field, Input, FormGrid } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import axios from 'axios';

const FormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.rLg};
  background: ${({ theme }) => theme.accentSoft};
  border: 1px solid ${({ theme }) => theme.accentLine};
`;

const InfoTitle = styled.h4`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.01em;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionLabel = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.text};
`;

const GridField = styled(Field)`
  margin-bottom: 0;
  min-width: 0;
`;

const InlineRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
`;

const PriceField = styled.div`
  flex: 0 0 100px;
  min-width: 0;
`;

const AccessCodeInput = styled(Input)`
  max-width: 160px;
  font-weight: 600;
  letter-spacing: 0.25em;
  text-align: center;
  text-transform: uppercase;
`;

const InlineFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

/** Generates a 5-character alphanumeric access code for student registration. */
export const generateCourseAccessCode = () =>
  Math.random().toString(36).substring(2, 7).toUpperCase();

const getBasicAuthHeader = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
};

/**
 * Course settings form — schedule, pricing, and registration options.
 * Reused inside the catalog settings modal and the legacy `/CourseSetting` route.
 */
const CourseSettingForm = ({
  courseDetails,
  onDismiss,
  onSaved,
  formId,
  onActionStateChange,
  showInlineFooter = true,
  saveDisabled = false,
}) => {
  const formatDateInput = (val) => (val ? String(val).split('T')[0] : '');

  const [startDate, setStartDate] = useState(formatDateInput(courseDetails?.start));
  const [endDate, setEndDate] = useState(formatDateInput(courseDetails?.expire));
  const [price, setPrice] = useState(courseDetails?.price || '');
  const [previousPrice, setPreviousPrice] = useState(courseDetails?.price || '');
  const [level, setLevel] = useState(courseDetails?.level || '');
  const [courseTime, setCourseTime] = useState(courseDetails?.courseTime || '');
  const [isLoading, setIsLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [appPurchase, setAppPurchase] = useState(Boolean(courseDetails?.price));
  const [updateAlertOpen, setUpdateAlertOpen] = useState(false);
  const [priceUpdateAlert, setPriceUpdateAlert] = useState(false);
  const inAppPurchaseEnable = localStorage.getItem('inAppPurchaseEnable');
  // In-app purchase pricing is available to all users. Actually enabling the
  // toggle still requires Stripe setup via `inAppPurchaseEnable`.
  const showInAppPurchase = true;
  const [useForRegisterStudents, setUseForRegisterStudents] = useState(
    Number(courseDetails?.registrationCourse) === 1 ? 1 : 0,
  );
  const [accessCode, setAccessCode] = useState(courseDetails?.accessCode || '');
  const [autoEnrol, setAutoEnrol] = useState(courseDetails?.autoEnrol || 0);
  const [active, setActive] = useState(Number(courseDetails?.active) === 1 ? 1 : 0);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!courseDetails) return;

    if (courseDetails.start) setStartDate(formatDateInput(courseDetails.start));
    if (courseDetails.expire) setEndDate(formatDateInput(courseDetails.expire));

    if (Number(courseDetails.registrationCourse) === 1) {
      setUseForRegisterStudents(1);
      setAccessCode(courseDetails.accessCode || generateCourseAccessCode());
    }

    setAutoEnrol(courseDetails.autoEnrol === 1 ? 1 : 0);
  }, [courseDetails]);

  useEffect(() => {
    if (onActionStateChange) onActionStateChange({ isLoading });
  }, [isLoading, onActionStateChange]);

  const handleAppPurchaseChange = (e) => {
    if (!inAppPurchaseEnable) {
      setAlertOpen(true);
      return;
    }
    const isChecked = e.target.checked;
    setAppPurchase(isChecked);
    if (!isChecked) setPrice(0);
  };

  const handlePriceChange = (e) => {
    setPreviousPrice(price);
    setPrice(e.target.value);
  };

  const handlePriceBlur = () => {
    if (price && price !== previousPrice) setPriceUpdateAlert(true);
  };

  const handleCancelPriceUpdate = () => {
    setPrice(courseDetails.price);
    setPriceUpdateAlert(false);
  };

  const handleConfirmPriceUpdate = () => {
    setPriceUpdateAlert(false);
  };

  const handleStripeOnboarding = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('email');
    const apiUrl1 = process.env.REACT_APP_API_URL1;
    const body = { email };

    try {
      const response = await axios.post(
        `${apiUrl1}/api/user/create-account-and-onboarding-link`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: token,
            'X-Api-Version': 'v2',
          },
        },
      );
      window.location.href = response.data.accountLink.url;
    } catch (error) {
      // legacy page swallowed errors silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (saveDisabled) return;
    setIsLoading(true);

    try {
      // Resetting per-assessment prices only matters for the in-app purchase flow,
      // and that endpoint is entitlement-gated. Skip it for plans without in-app
      // purchase so a plain settings save (schedule, level, registration) isn't blocked.
      if (showInAppPurchase) {
        await axios.put(`${apiUrl}/api/update-assessment-price/${courseDetails.courseId}`, {}, {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        });
      }

      const payload = {
        id: courseDetails.id,
        userId: courseDetails.userId,
        courseName: courseDetails.courseName,
        code: courseDetails.code,
        vendor: courseDetails.vendor,
        courseId: courseDetails.courseId,
        start: startDate,
        expire: endDate,
        isDeleted: courseDetails.isDeleted,
        active,
        orgId: courseDetails.orgId,
        level,
        price,
        about: courseDetails.about,
        courseTime,
        registrationCourse: useForRegisterStudents,
        accessCode,
        autoEnrol,
      };

      const response = await axios.put(`${apiUrl}/api/course/updatePrice`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });

      if (response.status === 200) setUpdateAlertOpen(true);
    } catch (error) {
      console.error('Failed to update course:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseUpdateAlert = () => {
    setUpdateAlertOpen(false);
    if (onSaved) onSaved();
    if (onDismiss) onDismiss();
  };

  if (!courseDetails) return null;

  return (
    <>
      <FormRoot id={formId} onSubmit={handleSubmit}>
        <InfoCard>
          <InfoTitle>{courseDetails.courseName || 'Course'}</InfoTitle>
          <InfoText>
            Define the course schedule, pricing, and visibility settings below.
          </InfoText>
        </InfoCard>

        <Section>
          <SectionLabel>Course status</SectionLabel>
          <InlineRow>
            <Toggle
              checked={active === 1}
              label={<LabelWithHelp help={HELP.courseEnabled}>Course enabled</LabelWithHelp>}
              onChange={(v) => setActive(v ? 1 : 0)}
            />
          </InlineRow>
        </Section>

        <Section>
          <SectionLabel>Schedule</SectionLabel>
          <FormGrid>
            <GridField>
              <label htmlFor="startDate"><LabelWithHelp help={HELP.startDate}>Start date</LabelWithHelp></label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(ev) => setStartDate(ev.target.value)}
                required
              />
            </GridField>
            <GridField>
              <label htmlFor="endDate"><LabelWithHelp help={HELP.endDate}>End date</LabelWithHelp></label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(ev) => setEndDate(ev.target.value)}
                required
              />
            </GridField>
          </FormGrid>
        </Section>

        <Section>
          <SectionLabel>Course details</SectionLabel>
          <FormGrid>
            <GridField>
              <label htmlFor="level"><LabelWithHelp help={HELP.level}>Level</LabelWithHelp></label>
              <Input
                id="level"
                type="number"
                value={level}
                onChange={(ev) => setLevel(ev.target.value)}
              />
            </GridField>
            <GridField>
              <label htmlFor="courseTime"><LabelWithHelp help={HELP.expiresInDays}>Expires in (days)</LabelWithHelp></label>
              <Input
                id="courseTime"
                type="number"
                min="0"
                value={courseTime}
                onChange={(ev) => setCourseTime(ev.target.value)}
              />
            </GridField>
          </FormGrid>
        </Section>

        {showInAppPurchase && (
        <Section>
          <SectionLabel>Pricing</SectionLabel>
          <InlineRow>
            <Toggle
              checked={appPurchase}
              onChange={(v) => handleAppPurchaseChange({ target: { checked: v } })}
              label={<LabelWithHelp help={HELP.inAppPurchase}>In-app purchase</LabelWithHelp>}
            />
            <PriceField>
              <GridField>
                <label htmlFor="price"><LabelWithHelp help={HELP.price}>Price</LabelWithHelp></label>
                <Input
                  id="price"
                  type="text"
                  value={price}
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  disabled={!appPurchase}
                  min={0}
                />
              </GridField>
            </PriceField>
          </InlineRow>
        </Section>
        )}

        <Section>
          <SectionLabel>Student registration</SectionLabel>
          <InlineRow>
              <Toggle
                checked={useForRegisterStudents === 1}
                label={<LabelWithHelp help={HELP.useForRegisterStudents}>Use for student registration</LabelWithHelp>}
                onChange={(v) => {
                  const checked = v ? 1 : 0;
                  setUseForRegisterStudents(checked);
                  if (checked) {
                    setAccessCode((prev) => prev || generateCourseAccessCode());
                  } else {
                    setAccessCode('');
                    setAutoEnrol(0);
                  }
                }}
              />

              {useForRegisterStudents === 1 && (
                <>
                  <GridField>
                    <label htmlFor="accessCode"><LabelWithHelp help={HELP.accessCode}>Access code</LabelWithHelp></label>
                    <AccessCodeInput
                      id="accessCode"
                      value={accessCode}
                      onChange={(ev) => {
                        const value = ev.target.value.replace(/[^A-Za-z0-9]/g, '');
                        setAccessCode(value.toUpperCase());
                      }}
                      maxLength={5}
                      placeholder="ABCDE"
                    />
                  </GridField>
                  <Toggle
                    checked={autoEnrol === 1}
                    label={<LabelWithHelp help={HELP.autoRegister}>Auto register</LabelWithHelp>}
                    onChange={(v) => setAutoEnrol(v ? 1 : 0)}
                  />
                </>
              )}
          </InlineRow>
        </Section>

        {showInlineFooter && (
          <InlineFoot>
            {onDismiss && (
              <Btn type="button" $variant="ghost" onClick={onDismiss} disabled={isLoading}>
                Cancel
              </Btn>
            )}
            <Btn type="submit" $variant="subtle" disabled={isLoading}>
              {isLoading ? 'Saving…' : 'Save changes'}
            </Btn>
          </InlineFoot>
        )}
      </FormRoot>

      <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
        <DialogTitle>Complete Stripe setup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To enable in-app purchases, you need to complete Stripe account setup first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleStripeOnboarding} color="primary" autoFocus disabled={isLoading}>
            {isLoading ? <ButtonSpinnerSlot size={24} /> : 'Complete Stripe setup'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={priceUpdateAlert} onClose={handleCancelPriceUpdate}>
        <DialogTitle>Confirm price update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All assessments associated with this course will be free if you add/update price in
            course. Do you want to update it?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPriceUpdate} color="secondary">Cancel</Button>
          <Button onClick={handleConfirmPriceUpdate} color="primary">OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateAlertOpen} onClose={handleCloseUpdateAlert}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>Course updated successfully!</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateAlert} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CourseSettingForm;
